import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useNotify } from "@/hooks/useNotify";
import { useUserInvitationStatus } from "@/hooks/useUserInvitationStatus";
import { useReactivationRequests } from "@/hooks/useReactivationRequests";
import { format } from "date-fns";
import InvitationLinkHandler from "@/components/auth/InvitationLinkHandler";
import ReactivationRequestCard from "@/components/reactivation/ReactivationRequestCard";
import { Settings, RefreshCw, CheckCircle, AlertCircle, Info } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

type DiagnosisResult = {
  action: string;
  message: string;
  timestamp: Date;
  pendingCount?: number;
  success: boolean;
};

const NoCompany = () => {
  const { signOut } = useAuth();
  const notify = useNotify();
  const { 
    pendingInvitations, 
    isLoading: invitationLoading, 
    acceptInvitation,
    refetch: refetchInvitations
  } = useUserInvitationStatus();
  
  const {
    deactivatedMemberships,
    isLoading: reactivationLoading,
    submitReactivationRequest,
    refetch: refetchReactivations
  } = useReactivationRequests();
  
  const [diagnosisResult, setDiagnosisResult] = useState<DiagnosisResult | null>(null);
  const [isRunningDiagnosis, setIsRunningDiagnosis] = useState(false);

  const refetch = async () => {
    await Promise.all([refetchInvitations(), refetchReactivations()]);
  };

  useEffect(() => {
    document.title = "No Company Access | Doyence Estimating";
  }, []);

  const handleFixAccount = async () => {
    setIsRunningDiagnosis(true);
    try {
      const { data, error } = await (supabase as any).rpc('self_fix_company_link');
      if (error) throw error;
      
      const action = (data as any)?.action || 'no_action';
      const message = (data as any)?.message;
      const pendingCount = (data as any)?.pending_invitations_count || 0;
      
      // Store diagnosis results
      const result: DiagnosisResult = {
        action,
        message: message || '',
        timestamp: new Date(),
        pendingCount,
        success: true
      };
      
      setDiagnosisResult(result);
      
      if (action === 'company_id_cleared') {
        notify.success("Account Fixed Successfully — Your company link has been reset. Check for new invitations or refresh to continue.");
        refetch();
      } else if (action === 'membership_exists') {
        notify.success("Valid Membership Found — Your company membership is valid. Refreshing page...");
        setTimeout(() => window.location.reload(), 1000);
      } else if (action === 'invitations_found') {
        notify.info(`Pending Invitations Found — Found ${pendingCount} pending invitation(s). Please accept them below.`);
        refetch();
      } else if (action === 'no_company_no_invitations') {
        notify.info("Account Diagnosed — No account issues found. You may need to request a new invitation or create a company.");
      } else {
        notify.info("Account Diagnosed — No account issues detected. Your account appears to be configured correctly.");
      }
    } catch (err: any) {
      const errorResult: DiagnosisResult = {
        action: 'error',
        message: err.message || 'Unable to fix account automatically. Please contact support if this persists.',
        timestamp: new Date(),
        success: false
      };
      setDiagnosisResult(errorResult);
      
      notify.error("Account Fix Failed", err.message || "Unable to fix account automatically. Please contact support if this persists.");
    } finally {
      setIsRunningDiagnosis(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col justify-center px-4 py-12">
      <header className="sm:mx-auto sm:w-full sm:max-w-2xl text-center mb-6">
        <h1 className="text-3xl font-bold tracking-tight text-foreground">No Company Access</h1>
        <p className="mt-2 text-muted-foreground">
          You are not currently associated with a company. You may have been removed or need to join/create a company.
        </p>
      </header>

      <main className="sm:mx-auto sm:w-full sm:max-w-4xl space-y-6">
        {/* Pending Invitations Section */}
        {pendingInvitations.length > 0 && (
          <section className="rounded-lg border bg-card text-card-foreground shadow-sm p-6">
            <h2 className="text-xl font-semibold mb-4">Pending Company Invitations</h2>
            <div className="space-y-3">
              {pendingInvitations.map((invitation) => (
                <div key={invitation.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex-1">
                    <h3 className="font-medium">{invitation.company_name}</h3>
                    <p className="text-sm text-muted-foreground">
                      Invited on {format(new Date(invitation.created_at), 'dd MMMM yyyy')}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Expires on {format(new Date(invitation.expires_at), 'dd MMMM yyyy')}
                    </p>
                  </div>
                  <Button 
                    onClick={() => acceptInvitation(invitation.id)}
                    disabled={invitationLoading}
                    className="ml-4"
                  >
                    Accept Invitation
                  </Button>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Deactivated Memberships Section */}
        {/* Deactivated Memberships Section */}
        {deactivatedMemberships.length > 0 && (
          <section className="rounded-lg border bg-card text-card-foreground shadow-sm p-6">
            <h2 className="text-xl font-semibold mb-4">Previous Company Memberships</h2>
            <div className="space-y-4">
              {deactivatedMemberships.map((membership) => (
                <ReactivationRequestCard
                  key={membership.id}
                  membership={membership}
                  onSubmitRequest={submitReactivationRequest}
                  isLoading={reactivationLoading}
                />
              ))}
            </div>
          </section>
        )}

        {/* Main Actions Section */}
        <section className="rounded-lg border bg-card text-card-foreground shadow-sm p-6">
          <article className="space-y-4">
            {pendingInvitations.length === 0 && deactivatedMemberships.length === 0 && (
              <p>
                To continue, either join a company using an invitation link or create a new company.
              </p>
            )}
            {(pendingInvitations.length > 0 || deactivatedMemberships.length > 0) && (
              <p>
                You can also join with an invitation link, create a new company, or contact support if you need help.
              </p>
            )}
            <div className="flex flex-col sm:flex-row gap-3">
              <InvitationLinkHandler className="w-full" variant="default" />
              <Link to="/company-creation" className="sm:w-auto w-full">
                <Button className="w-full" variant="outline">Create new company</Button>
              </Link>
              <Button 
                className="sm:w-auto w-full" 
                variant="secondary" 
                onClick={handleFixAccount}
                disabled={invitationLoading || reactivationLoading || isRunningDiagnosis}
              >
                {isRunningDiagnosis ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Settings className="h-4 w-4 mr-2" />
                )}
                {isRunningDiagnosis ? 'Running Diagnosis...' : 'Diagnose Account'}
              </Button>
              <Button className="sm:w-auto w-full" variant="secondary" onClick={() => signOut()}>Sign out</Button>
            </div>
            
            {/* Diagnosis Results Section */}
            {diagnosisResult && (
              <Card className="mt-4">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    {diagnosisResult.success ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : (
                      <AlertCircle className="h-5 w-5 text-red-600" />
                    )}
                    Diagnosis Results
                  </CardTitle>
                  <CardDescription>
                    Last run: {format(diagnosisResult.timestamp, 'dd MMMM yyyy HH:mm')}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-start gap-2">
                    <Info className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                    <div className="space-y-2">
                      <p className="text-sm font-medium">
                        {diagnosisResult.action === 'company_id_cleared' && 'Company link was reset'}
                        {diagnosisResult.action === 'membership_exists' && 'Valid company membership found'}
                        {diagnosisResult.action === 'invitations_found' && `Found ${diagnosisResult.pendingCount} pending invitation(s)`}
                        {diagnosisResult.action === 'no_company_no_invitations' && 'No account issues detected'}
                        {diagnosisResult.action === 'error' && 'Diagnosis failed'}
                        {!['company_id_cleared', 'membership_exists', 'invitations_found', 'no_company_no_invitations', 'error'].includes(diagnosisResult.action) && 'Account diagnosis completed'}
                      </p>
                      {diagnosisResult.message && (
                        <p className="text-sm text-muted-foreground">
                          {diagnosisResult.message}
                        </p>
                      )}
                      
                      {/* Next Steps */}
                      <div className="mt-3">
                        <p className="text-sm font-medium mb-1">Recommended next steps:</p>
                        <div className="text-sm text-muted-foreground">
                          {diagnosisResult.action === 'company_id_cleared' && (
                            <p>• Refresh this page or check for new invitations above</p>
                          )}
                          {diagnosisResult.action === 'membership_exists' && (
                            <p>• Page will refresh automatically to load your company access</p>
                          )}
                          {diagnosisResult.action === 'invitations_found' && (
                            <p>• Accept the pending invitations shown above</p>
                          )}
                          {diagnosisResult.action === 'no_company_no_invitations' && (
                            <>
                              <p>• Request an invitation from your company administrator</p>
                              <p>• Or create a new company using the button above</p>
                            </>
                          )}
                          {diagnosisResult.action === 'error' && (
                            <p>• Try running the diagnosis again or contact support</p>
                          )}
                        </div>
                      </div>
                      
                      {diagnosisResult.action !== 'error' && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => window.location.reload()}
                          className="mt-2"
                        >
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Refresh Page
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Additional Help Section */}
            <div className="mt-4 p-4 bg-muted/50 rounded-lg">
              <h3 className="font-medium text-sm mb-2">Need Help?</h3>
              <div className="text-sm text-muted-foreground space-y-1">
                <p><strong>Join with invitation link:</strong> Use this if you received an invitation URL via email or other communication.</p>
                <p><strong>Create new company:</strong> Start fresh by setting up your own company profile.</p>
                <p><strong>Diagnose Account:</strong> Check for account issues or find pending invitations that might not be showing.</p>
              </div>
            </div>
          </article>
        </section>
      </main>
    </div>
  );
};

export default NoCompany;
